import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PublicSubprojectListItem from './PublicSubprojectListItem';
import { SectionTitle } from '../../../components/typography/Heading';
import ShowMore from '../../../components/show-more/ShowMore';
import InputSearchCompact from '../../../components/input/input-search-compact/InputSearchCompact';
import { canUserEditProjectSelector } from '../../../user/user-selectors';
import { fetchSubprojectGenerationLinkForProject, fetchSubprojectsForProjectPublic } from '../subproject-actions';
import { getSubprojectsForProject, subprojectsAreTheSame } from '../../subproject-selectors';
import styles from './public-subproject-display.module.less';
import moment from 'moment';
import { isProjectADugnad } from '../../project-model';
import { useProject } from '../../project-hooks/useProject';
import { Link } from 'react-router';
import { createUrlFromCreationUUID } from '../../subproject-model';

const PublicSubprojectDisplay = ({ projectId }) => {
    const dispatch = useDispatch();
    const subprojects = useSelector((state) => getSubprojectsForProject(state, projectId), {
        equalityFn: subprojectsAreTheSame,
    });
    const project = useProject();
    const isDugnad = isProjectADugnad(project);
    const [createSubprojectURL, setCreateSubprojectURL] = useState();
    const canUserEditThisProject = useSelector((state) => canUserEditProjectSelector(state, projectId));
    const [currentSearchText, setCurrentSearchText] = useState('');
    const payToRegisterSubproject = project?.profile?.payToRegisterSubproject;
    const showCollectedForSubprojects = project?.profile?.showCollectedForSubprojects;
    const sortedSubprojects = [...subprojects].sort((a, b) => {
        if (payToRegisterSubproject && showCollectedForSubprojects) {
            return b.collected - a.collected;
        }
        const publishedAtA = moment(a.published_at);
        const publishedAtB = moment(b.published_at);

        if (publishedAtA.isBefore(publishedAtB)) {
            return -1;
        }
        if (publishedAtA.isAfter(publishedAtB)) {
            return 1;
        }
        return 0;
    });

    useEffect(() => {
        dispatch(fetchSubprojectsForProjectPublic(projectId));
        if (!isDugnad) {
            dispatch(fetchSubprojectGenerationLinkForProject({ projectId, pub: true })).then((result) =>
                setCreateSubprojectURL(
                    createUrlFromCreationUUID({ uuid: result.creation_uuid, projectId, relative: true })
                )
            );
        }
    }, [projectId, isDugnad, dispatch]);

    if (sortedSubprojects.length === 0 && isDugnad) {
        return null;
    }

    const getFilteredSubProjects = (currentSearchText, sortedSubprojects) => {
        if (currentSearchText.length > 0) {
            return sortedSubprojects.filter((subproject) =>
                subproject.name?.toLowerCase().includes(currentSearchText.toLowerCase())
            );
        }
        if (canUserEditThisProject) {
            return [...sortedSubprojects, { id: 0, invite: true }];
        }
        if (!isDugnad && !project?.profile?.payToRegisterSubproject) {
            return [{ id: 0, joinUrl: createSubprojectURL }, ...sortedSubprojects];
        }

        return sortedSubprojects;
    };

    const filteredSubprojects = getFilteredSubProjects(currentSearchText, sortedSubprojects);

    return (
        <div className={styles.subprojectsContainer}>
            <SectionTitle tag="h2">
                {project.profile?.subprojectHeading ||
                    `Disse er med på ${isDugnad ? 'dugnaden' : payToRegisterSubproject ? 'konkurransen' : 'laget'}`}
            </SectionTitle>
            {sortedSubprojects.length > 6 && (
                <InputSearchCompact
                    type={'search'}
                    name={'search'}
                    title={'Finn en deltaker'}
                    value={currentSearchText}
                    onChange={(e) => setCurrentSearchText(e.target.value)}
                />
            )}
            <div className={styles.subprojectListContainer}>
                {!filteredSubprojects || filteredSubprojects.length === 0 ? (
                    currentSearchText.length === 0 ? (
                        <p>
                            Ingen deltakere har meldt seg på enda.{' '}
                            {!isDugnad && !payToRegisterSubproject && <Link to={createSubprojectURL}>Meld deg på</Link>}
                        </p>
                    ) : (
                        <p>
                            Fant ingen deltakere.{' '}
                            {!isDugnad && !payToRegisterSubproject && <Link to={createSubprojectURL}>Meld deg på</Link>}
                        </p>
                    )
                ) : (
                    <ShowMore
                        showCount={project.profile?.subProjectShowCount || 5}
                        items={filteredSubprojects}
                        listCreator={(subprojects) => {
                            return subprojects?.map((subproject) => (
                                <PublicSubprojectListItem
                                    key={subproject.id}
                                    subproject={subproject}
                                    projectId={projectId}
                                    invite={subproject.invite}
                                    joinUrl={subproject.joinUrl}
                                    showCollected={showCollectedForSubprojects}
                                />
                            ));
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default PublicSubprojectDisplay;
